// AuvoReportsSection.tsx
import React, { useEffect, useState } from "react";
import ArrowDownIcon from "../../assets/icons/arrow-down.svg";
import { useTheme } from "../../context/ThemeContext";

type AuvoTask = {
  taskID?: number;
  taskDate?: string;
  taskUrl?: string;
  userToName?: string;
  customerDescription?: string;
  finished?: boolean;
  [key: string]: any;
};

type AuvoReportsSectionProps = {
  os: string | null;
};

// Token inicial (já com "Bearer ")
const AUVO_TOKEN =
  "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1bmlxdWVfbmFtZSI6WyJjcmlzdGlhbm8uZW5oYXJpYS5hY0BnbWFpbC5jb20iLCJjcmlzdGlhbm8uZW5oYXJpYS5hY0BnbWFpbC5jb20iXSwianRpIjoiNzcwMDNlMDE4Mzk3NGI1ZjkwMmViM2U4YjZlZTliMWYiLCJhcGlLZXkiOiJKZEY1ZFloZzIwaGllMldkWEVwU3F6ZlY1Q1AyaEhpIiwiYXBpVG9rZW4iOiJtWVVBYzhjaTNraVpiTWhxSklSQVM2bjNuR1lsMWdhIiwibmJmIjoxNzYzMDYzMTIzLCJleHAiOjE3NjMwNzg3NjMsImlhdCI6MTc2MzA2MzEyMywiaXNzIjoiQXV2b19BcGlfUHVibGljYSIsImF1ZCI6IlVzdWFyaW9fQXBpIn0.ivz7l7-gIV1dMGmwlE6TXhDAIghYqEXfZo39DOA9dY";

// Credenciais para renovar o token (login Auvo)
const AUVO_API_KEY = "JdF5dYhg20hie2WdXEpSqzfV5CP2hHi";
const AUVO_API_TOKEN = "mYUAc8ci3kiZbMhqJIRAS6n3nGYl1ga";

const AuvoReportsSection: React.FC<AuvoReportsSectionProps> = ({ os }) => {
  const [tasks, setTasks] = useState<AuvoTask[] | null>(null);
  const [loading, setLoading] = useState(false);

  // Header de autorização atual (sempre "Bearer " + token)
  const [authHeader, setAuthHeader] = useState<string>(AUVO_TOKEN);

  // Abre/fecha a lista (começa fechado)
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { theme } = useTheme();
  const BLUE = theme?.primary || "#124280"; // 👈 agora vem do tema
  const HOVER_BG = "#E9F0FA";
  const BORDER = "black";

  const FONT_FAMILY =
    'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

  useEffect(() => {
    let cancelled = false;

    async function loginAuvo(): Promise<string> {
      const loginRes = await fetch("https://api.auvo.com.br/v2/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          apiKey: AUVO_API_KEY,
          apiToken: AUVO_API_TOKEN,
        }),
      });

      if (!loginRes.ok) {
        throw new Error(`Login Auvo falhou: HTTP ${loginRes.status}`);
      }

      const loginJson = await loginRes.json().catch(() => ({}));
      const newAccessToken = loginJson?.result?.accessToken;

      if (!newAccessToken) {
        throw new Error("Login Auvo não retornou accessToken.");
      }

      const newHeader = `Bearer ${newAccessToken}`;
      if (!cancelled) {
        setAuthHeader(newHeader);
      }

      return newHeader;
    }

    async function fetchTasksWithAuth(
      header: string,
      alreadyRefreshed: boolean
    ): Promise<Response> {
      const paramFilter = {
        orientation: os,
        startDate: "2025-01-01T00:00:00",
        endDate: "2025-12-30T23:59:59",
      };

      const url = `https://api.auvo.com.br/v2/tasks/?paramFilter=${encodeURIComponent(
        JSON.stringify(paramFilter)
      )}&page=1&pageSize=10&order=1&selectfields=`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: header,
        },
      });

      if (res.status === 401 && !alreadyRefreshed) {
        const newHeader = await loginAuvo();
        const retryRes = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: newHeader,
          },
        });

        if (!retryRes.ok) {
          throw new Error(`HTTP ${retryRes.status}`);
        }

        return retryRes;
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      return res;
    }

    async function loadTasks() {
      if (!os) {
        setTasks(null);
        return;
      }

      setLoading(true);
      try {
        const res = await fetchTasksWithAuth(authHeader, false);
        const json = await res.json().catch(() => ({}));
        const list = json?.result?.entityList || [];

        if (!cancelled) {
          setTasks(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        console.error("Erro ao buscar dados do Auvo:", e);
        if (!cancelled) setTasks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTasks();

    return () => {
      cancelled = true;
    };
  }, [os, authHeader]);

  if (!os) return null;

  // --- Estado "carregando" (mantém o mesmo cabeçalho com texto suave) ---
  if (loading && (!tasks || tasks.length === 0)) {
    return (
      <div
        style={{
          marginTop: 10,
          border: `1px solid ${BORDER}`,
          borderRadius: 0,
          background: "#ffffff",
          fontFamily: FONT_FAMILY,
        }}
      >
        <div
          onClick={() => setIsOpen((prev) => !prev)}
          style={{
            padding: "12px 10px",
            background: BLUE,
            color: "#ffffff",
            borderBottom: `1px solid ${BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            cursor: "pointer",
            userSelect: "none",
            flexDirection: "row",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <span
              style={{
                fontWeight: 400,
                letterSpacing: "0.03em",
                fontSize: 14,
              }}
            >
              Relatórios digitais (Auvo)
            </span>
            <span
              style={{
                fontWeight: 300,
                fontSize: 11,
                opacity: 0.9,
              }}
            >
              clique aqui para verificar os relatórios
            </span>
          </div>
        </div>

        {isOpen && (
          <div
            style={{
              padding: "10px 14px",
              fontSize: 14,
              color: "#4b5563",
            }}
          >
            Carregando relatórios digitais (Auvo)...
          </div>
        )}
      </div>
    );
  }

  if (!tasks || tasks.length === 0) return null;

  const ordered = [...tasks].sort(
    (a, b) =>
      new Date(b.taskDate || "").getTime() -
      new Date(a.taskDate || "").getTime()
  );

  return (
    <div
      style={{
        marginTop: 10,
        border: `1px solid ${BORDER}`,
        borderRadius: 0,
        background: "#ffffff",
        fontFamily: FONT_FAMILY,
        overflow: "hidden",
      }}
    >
      {/* Cabeçalho com seta / toggle (mais suave e profissional) */}
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          padding: "14px 10px",
          background: BLUE,
          color: "#ffffff",
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          cursor: "pointer",
          userSelect: "none",
        }}
        aria-expanded={isOpen}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <span
            style={{
              fontWeight: 400,
              letterSpacing: "0.03em",
              fontSize: 14,
            }}
          >
            Relatórios digitais (Auvo)
          </span>
          <span
            style={{
              fontWeight: 300,
              fontSize: 11,
              opacity: 0.9,
            }}
          >
            clique aqui para verificar os relatórios
          </span>
        </div>
      </div>

      {/* Conteúdo colapsável com animação */}
      <div
        style={{
          padding: 0,
          overflow: "hidden",
          maxHeight: isOpen ? 600 : 0,
          transition: "max-height 220ms ease",
        }}
      >
        {ordered.map((t, idx) => {
          let dt = "-";
          try {
            if (t.taskDate)
              dt = new Date(t.taskDate).toLocaleDateString("pt-BR");
          } catch {}

          const techName = t.userToName || "Técnico não informado";
          const vessel = t.customerDescription || "";
          const isFinished = t.finished === true;

          const handleClick = () => {
            if (t.taskUrl) window.open(t.taskUrl, "_blank");
          };

          return (
            <button
              key={t.taskID ?? idx}
              onClick={handleClick}
              style={{
                margin: 0,
                padding: 0,
                width: "100%",
                border: "none",
                background: "transparent",
                appearance: "none",
                WebkitAppearance: "none",
                outline: "none",
                boxShadow: "none",
                cursor: t.taskUrl ? "pointer" : "default",
              }}
              onMouseEnter={(e) => {
                const inner = e.currentTarget.firstElementChild as HTMLElement;
                if (inner) inner.style.background = HOVER_BG;
              }}
              onMouseLeave={(e) => {
                const inner = e.currentTarget.firstElementChild as HTMLElement;
                if (inner) inner.style.background = "#ffffff";
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "10px 12px",
                  background: "#ffffff",
                  borderBottom:
                    idx === ordered.length - 1 ? "none" : `1px solid ${BORDER}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    marginBottom: vessel ? 2 : 0,
                  }}
                >
                  <span style={{ flex: 1, color: "#111827" }}>
                    <b>Acessar Relatório Digital</b>{" "}
                    <span style={{ color: "#4b5563" }}>— {dt}</span>{" "}
                    <span style={{ color: "#6b7280" }}>
                      · Técnico: <b>{techName}</b>
                      {isFinished ? " (finalizado)" : ""}
                    </span>
                  </span>
                </div>

                {vessel && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginLeft: 28,
                    }}
                  >
                    Embarcação: {vessel}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AuvoReportsSection;
